/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { MediaHelper } from "../../helpers/mediaHelper";
import { StorageHelper } from "../../helpers/storageHelper";
import { TimeHelper } from "../../helpers/timeHelper";
import { IMediaModel, IUserAssetPropertiesModel } from "../../models";

interface IProgresses {
  progress: number;
  progressInSeconds: number;
}

const NO_PROGRESS: IProgresses = {
  progress: -1,
  progressInSeconds: -1,
};

const hasProgress = (progresses: IProgresses): boolean =>
  progresses.progress !== NO_PROGRESS.progress;

const hasIdentifier = (id?: IMediaModel["Id"]): id is IMediaModel["Id"] =>
  id !== undefined && id !== null;

const isSameIdentifier = (
  first?: IMediaModel["Id"],
  second?: IMediaModel["Id"],
): boolean =>
  hasIdentifier(first) &&
  hasIdentifier(second) &&
  String(first) === String(second);

const getProgressMedia = (media?: IMediaModel): IMediaModel | undefined => {
  if (!hasIdentifier(media?.PlayableMediaId)) {
    return media;
  }

  const playableMedia = media.Media?.find((row) =>
    isSameIdentifier(row.Id, media.PlayableMediaId),
  );

  return {
    ...media,
    ...playableMedia,
    Id: media.PlayableMediaId,
    MediaTypeCode:
      playableMedia?.MediaTypeCode ??
      media.PlayableMediaTypeCode ??
      media.MediaTypeCode,
    Duration: playableMedia?.Duration ?? media.Duration,
    StartDateTime: playableMedia?.StartDateTime ?? media.StartDateTime,
    EndDateTime: playableMedia?.EndDateTime ?? media.EndDateTime,
  };
};

const getProgressDuration = (media?: IMediaModel): number | undefined => {
  if (media?.Duration) {
    return media.Duration;
  }

  if (media?.StartDateTime && media.EndDateTime) {
    const durationInSeconds = TimeHelper.getDuration(
      undefined,
      media.StartDateTime,
      media.EndDateTime,
    );

    return durationInSeconds > 0 ? durationInSeconds * 1000 : undefined;
  }

  return undefined;
};

export const useMediaProgress = (media?: IMediaModel) => {
  const isMounted = useRef(false);
  const [progresses, setProgresses] = useState<IProgresses>(NO_PROGRESS);
  const progressMedia = useMemo(() => getProgressMedia(media), [media]);
  const liveProgress = useMemo(() => {
    if (!MediaHelper.isLiveMediaType(media?.MediaTypeCode)) {
      return NO_PROGRESS;
    }

    return MediaHelper.getLiveProgress(media);
  }, [media]);
  const hasLiveProgress = hasProgress(liveProgress);
  const isProgressMediaLive = MediaHelper.isLiveMediaType(
    progressMedia?.MediaTypeCode,
  );

  const getStoredProgress = useCallback(
    (userAssetProperties: IUserAssetPropertiesModel[]): IProgresses => {
      const duration = getProgressDuration(progressMedia);

      if (hasIdentifier(progressMedia?.Id) && duration && userAssetProperties) {
        const mediaProperties = userAssetProperties.find((row) =>
          isSameIdentifier(row.AssetId, progressMedia.Id),
        );

        if (mediaProperties?.Timestamp) {
          const { Hour, Minute, Second } = mediaProperties.Timestamp;
          const progressInMiliseconds =
            ((Hour ?? 0) * 3600 + (Minute ?? 0) * 60 + (Second ?? 0)) * 1000;
          const progressInSeconds =
            (Hour ?? 0) * 3600 + (Minute ?? 0) * 60 + (Second ?? 0);

          const progress =
            (progressInMiliseconds / (Math.floor(duration / 1000) * 1000)) *
            100;
          return {
            progress,
            progressInSeconds,
          };
        } else if (mediaProperties?.ProgressPercent) {
          const progressInSeconds =
            (duration * mediaProperties?.ProgressPercent) / 100;
          return {
            progress: mediaProperties?.ProgressPercent,
            progressInSeconds,
          };
        }
      }

      return NO_PROGRESS;
    },
    [progressMedia],
  );

  const calcProgress = useCallback(async (): Promise<IProgresses> => {
    if (!hasIdentifier(progressMedia?.Id)) {
      return NO_PROGRESS;
    }

    if (hasLiveProgress) {
      return liveProgress;
    }

    if (isProgressMediaLive) {
      return MediaHelper.getLiveProgress(progressMedia);
    }

    if (getProgressDuration(progressMedia)) {
      const userAssetProperties = await StorageHelper.getUserAssetsProperties();
      return getStoredProgress(userAssetProperties);
    }

    return NO_PROGRESS;
  }, [
    getStoredProgress,
    hasLiveProgress,
    isProgressMediaLive,
    liveProgress,
    progressMedia,
  ]);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (hasLiveProgress || isProgressMediaLive) {
      return;
    }

    const subscription = StorageHelper.userAssetProperties$.subscribe(
      (value: IUserAssetPropertiesModel[]) => {
        if (isMounted.current) {
          setProgresses(getStoredProgress(value));
        }
      },
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [getStoredProgress, hasLiveProgress, isProgressMediaLive]);

  useEffect(() => {
    calcProgress()
      .then((progresses) => {
        if (isMounted.current) {
          setProgresses(progresses);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }, [calcProgress]);

  return progresses;
};
